<template>
    <div class="modal fade" id="authenticatedActionModal" tabindex="-1" aria-labelledby="authenticatedActionModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered popOutModal">
            <div class="modal-content">
                <div ref="authenticatedActionModal" class="modal-body d-flex flex-column py-4 align-items-center justify-content-between">
	                <ConfirmPassword v-if="!isPasswordVerified" :auto-submit="false" @close-password-confirmation="close" @password-verification-status="passwordVerificationCheck"></ConfirmPassword>
	                <Verification :type="twoFactorMethod" purpose="two_factor" :user_id="user.id" :timer="expirySeconds" :allow-switching="canSwitchTwoFactorMethod" :allow_recovery_code="canUseRecoveryCodes" v-if="isPasswordVerified && !isTwoFactorVerified" @close-verification="close" @confirmation-status="twoFactorVerificationCheck"></Verification>
	                <ComponentViewEmail v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'email_view'" @close-view-email="close" ></ComponentViewEmail>
	                <ComponentChangeEmail v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'email_change'" @close-change-email="close" ></ComponentChangeEmail>
	                <ComponentChangeUsername v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'username_change'" @close-change-username="close" ></ComponentChangeUsername>
	                <ComponentChangePassword v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'password_change'" @close-change-password="close" ></ComponentChangePassword>
	                <ComponentDeleteAccount v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'delete_account'" @close-delete-account="close" ></ComponentDeleteAccount>
	                <ComponentDisableTwoFactor v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'two_factor_disable'" @close-disable-two-factor="close" ></ComponentDisableTwoFactor>
	                <ComponentEnableTwoFactor v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'two_factor_enable'" @close-enable-two-factor="close" ></ComponentEnableTwoFactor>
	                <ComponentViewQrCode v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'two_factor_qr_code'" @close-view-q-r-code="close" ></ComponentViewQrCode>
	                <ComponentViewRecoveryCode v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'two_factor_recovery_code'" @close-view-recovery-code="close" ></ComponentViewRecoveryCode>
	                <ComponentRegenerateRecoveryCodes v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'two_factor_regenerate_recovery_code'" @close-regenerate-recovery-codes="close"></ComponentRegenerateRecoveryCodes>
	                <ComponentDeleteRecoveryCodes v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'two_factor_delete_recovery_code'" @close-delete-recovery-codes="close"></ComponentDeleteRecoveryCodes>
	                <ComponentRevokeSingle v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'revoke_single_session'" :data="data" @close-revoke-single="close"></ComponentRevokeSingle>
	                <ComponentRevokeAll v-if="(isPasswordVerified && isTwoFactorVerified) && action === 'revoke_all_sessions'" @close-revoke-all="close"></ComponentRevokeAll>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import Modal from "bootstrap/js/dist/modal";
import ConfirmPassword from "@/components/GenericModalAndViews/ConfirmPassword.vue";
import { useAuthStore } from "@/stores/auth-store.js";
import Verification from "@/components/GenericModalAndViews/Verification.vue";
import ComponentChangeEmail from "@/components/Member/Settings/Account/ComponentChangeEmail.vue";
import apiClient from "@/utils/axios.js";
import { useToast } from "@/composables/useToast.js";
import ComponentChangeUsername from "@/components/Member/Settings/Account/ComponentChangeUsername.vue";
import ComponentChangePassword from "@/components/Member/Settings/Account/ComponentChangePassword.vue";
import ComponentViewEmail from "@/components/Member/Settings/Account/ComponentViewEmail.vue";
import ComponentDisableTwoFactor from "@/components/Member/Settings/TwoFactorAuthentication/ComponentDisableTwoFactor.vue";
import ComponentEnableTwoFactor from "@/components/Member/Settings/TwoFactorAuthentication/ComponentEnableTwoFactor.vue";
import ComponentViewQrCode from "@/components/Member/Settings/TwoFactorAuthentication/ComponentViewQRCode.vue";
import ComponentViewRecoveryCode from "@/components/Member/Settings/TwoFactorAuthentication/ComponentViewRecoveryCode.vue";
import ComponentRegenerateRecoveryCodes from "@/components/Member/Settings/TwoFactorAuthentication/ComponentRegenerateRecoveryCodes.vue";
import ComponentDeleteRecoveryCodes from "@/components/Member/Settings/TwoFactorAuthentication/ComponentDeleteRecoveryCodes.vue";
import { useAppHelpers } from "@/composables/useAppHelpers.js";
import ComponentRevokeSingle from "@/components/Member/Settings/SessionsAndTrustedDevices/ComponentRevokeSingle.vue";
import ComponentRevokeAll from "@/components/Member/Settings/SessionsAndTrustedDevices/ComponentRevokeAll.vue";
import ComponentDeleteAccount from "@/components/Member/Settings/Account/ComponentDeleteAccount.vue";

const props = defineProps({
	action: { type: String, required: true },
	considerTrustedDevice: { type: Boolean, required: false, default: true },
	canUseRecoveryCodes: { type: Boolean, required: false, default: false },
	canSwitchTwoFactorMethod: { type: Boolean, required: false, default: false },
	reloadUser: { type: Boolean, required: false, default: true },
	data: { type: null, required: false, default: null }
});

const emits = defineEmits(['performAction', 'closeModal']);
const auth = useAuthStore();
const helper = useAppHelpers();
const { displayToast } = useToast();

const modal = ref(null);
const authenticatedActionModal = ref(null);

const isPasswordVerified = ref(false);
const isTwoFactorVerified = ref(false);
const isTrustedDevice = ref(false);
const twoFactorMethod = ref('email');
const expirySeconds = ref(300);

const user = ref(auth.user);

watch(() => auth.user.is_trusted_device, (newVal) => {
	updateTrustedDevice(newVal);
});

watch(() => auth.user.is_two_factor_enabled, (newVal) => {
	updateTwoFactorMethod(newVal);
});

watch(() => auth.user, (newVal) => {
	user.value = newVal;
});

onMounted(() => {
	const modalEl = document.getElementById('authenticatedActionModal');
	modal.value = Modal.getOrCreateInstance(modalEl);
    modal.value.show();

    modalEl.addEventListener('hide.bs.modal', function (event) {
        modalEl.querySelector('.modal-dialog').classList.add('closing');
        setTimeout(() => {
            modalEl.querySelector('.modal-dialog').classList.remove('closing');
        }, 500)
        emits('closeModal');
    });

	updateTrustedDevice(auth.user.is_trusted_device);
	updateTwoFactorMethod(auth.user.is_two_factor_enabled);
});

const updateTrustedDevice = (status) => {
	isTrustedDevice.value = status;
	if(isTrustedDevice.value && props.considerTrustedDevice) {
		isTwoFactorVerified.value = true;
	}
}
const updateTwoFactorMethod = (status) => {
	if(status){
		twoFactorMethod.value = 'two_factor'
	}
	else{
		twoFactorMethod.value = 'email'
	}
}

const passwordVerificationCheck = async (status) => {
	if(status){
		if(!isTrustedDevice.value && twoFactorMethod.value !== 'two_factor'){
			await sendEmailTwoFactorCode();
		}
		isPasswordVerified.value = true;
	}
}

const twoFactorVerificationCheck = async (status) => {
	if(status[0]){
		isTwoFactorVerified.value = true;
	}
}

const sendEmailTwoFactorCode = async () => {
	try{
		const response = await apiClient.post('auth/resendVerificationCodeEmail', {
			purpose: 'two_factor_email'
		});
		if (response.status >= 200 && response.status < 300) {
			expirySeconds.value = response.data.expire_seconds;
		}

	}
	catch (error) {
	}
}

const close = async () => {
	if(props.reloadUser){
		helper.loaderOn(authenticatedActionModal.value);
		await auth.FETCH_USER_DATA(true);
		helper.loaderOff(authenticatedActionModal.value);
	}
    if (modal.value) {
        modal.value.hide();
    }
}

defineExpose({
    close
});
</script>